import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { countermeasureReviewSchema } from '@/features/drbfm/schemas/countermeasureReview.schema';
import { fieldErrorsOf } from '@/lib/forms/fieldErrors';
import { useInteracted } from '@/lib/forms/useInteracted';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import type {
  Countermeasure,
  UpdateCountermeasureReviewRequest,
} from '../../../../types/drbfm';
import { CARD_INPUT, CARD_TEXTAREA, REJECT_BUTTON, SCORE_INPUT } from './styles';

export type ReviewCountermeasure = (
  id: string,
  request: UpdateCountermeasureReviewRequest,
) => void | Promise<void>;

interface CountermeasureCardProps {
  measure: Countermeasure;
  isLoading: boolean;
  onReview: ReviewCountermeasure;
}

const CountermeasureCard: React.FC<CountermeasureCardProps> = ({
  measure,
  isLoading,
  onReview,
}) => {
  const { t } = useTranslation();
  const { isTouched, onEdit } = useInteracted();
  const [description, setDescription] = React.useState(measure.description);
  const [targetCause, setTargetCause] = React.useState(measure.targetCause || '');
  const [mechanism, setMechanism] = React.useState(measure.mechanism || '');
  const [verificationMethod, setVerificationMethod] = React.useState(
    measure.verificationMethod || '',
  );
  const [acceptanceCriteria, setAcceptanceCriteria] = React.useState(
    measure.acceptanceCriteria || '',
  );
  const [effectiveness, setEffectiveness] = React.useState<number | ''>(
    measure.validatedEffectivenessScore || '',
  );
  const [feasibility, setFeasibility] = React.useState<number | ''>(measure.feasibilityScore || '');

  const reviewValues = {
    description,
    targetCause,
    mechanism,
    verificationMethod,
    acceptanceCriteria,
    validatedEffectivenessScore: effectiveness === '' ? undefined : effectiveness,
    feasibilityScore: feasibility === '' ? undefined : feasibility,
  };
  const parsed = countermeasureReviewSchema.safeParse(reviewValues);
  const incomplete = !parsed.success;
  const errors = fieldErrorsOf(parsed);

  const save = () => onReview(measure.id, { reviewStatus: 'modified', ...reviewValues });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary" className="revy-cjk">{measure.countermeasureType}</Badge>
        <Badge variant="outline" className="revy-cjk">
          {measure.reviewStatus === 'ai_candidate'
            ? t('drbfm.workflow.cards.aiCandidate', 'AI候補')
            : measure.reviewStatus}
        </Badge>
        <Badge variant="outline" className="revy-cjk revy-figure">
          {t('drbfm.workflow.cards.aiExpectedEffect', 'AI期待効果 {{score}}', {
            score: measure.effectivenessScore,
          })}
        </Badge>
      </div>

      <Field label={t('drbfm.workflow.cards.countermeasure', '対策')} required error={errors.description} errorLive={isTouched('description')}>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            className={CARD_TEXTAREA}
            value={description}
            onChange={onEdit('description', (event) => setDescription(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.targetCause', '対象原因')} required error={errors.targetCause} errorLive={isTouched('targetCause')}>
        {(control) => (
          <Input
            {...control}
            className={CARD_INPUT}
            value={targetCause}
            onChange={onEdit('targetCause', (event) => setTargetCause(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.mechanism', '作用機序')} required error={errors.mechanism} errorLive={isTouched('mechanism')}>
        {(control) => (
          <Input
            {...control}
            className={CARD_INPUT}
            value={mechanism}
            onChange={onEdit('mechanism', (event) => setMechanism(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.verificationMethod', '検証方法')} required error={errors.verificationMethod} errorLive={isTouched('verificationMethod')}>
        {(control) => (
          <Input
            {...control}
            required
            className={CARD_INPUT}
            value={verificationMethod}
            onChange={onEdit('verificationMethod', (event) => setVerificationMethod(event.target.value))}
          />
        )}
      </Field>
      <Field label={t('drbfm.workflow.cards.acceptanceCriteria', '合格基準')} required error={errors.acceptanceCriteria} errorLive={isTouched('acceptanceCriteria')}>
        {(control) => (
          <Input
            {...control}
            required
            className={CARD_INPUT}
            value={acceptanceCriteria}
            onChange={onEdit('acceptanceCriteria', (event) => setAcceptanceCriteria(event.target.value))}
          />
        )}
      </Field>

      <div className="flex flex-col gap-3 min-[600px]:flex-row">
        <Field
          label={t('drbfm.workflow.cards.confirmedEffect', '確認済み効果')}
          className="min-[600px]:w-[200px]"
          error={errors.validatedEffectivenessScore}
          errorLive={isTouched('validatedEffectivenessScore')}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              max={10}
              className={SCORE_INPUT}
              value={effectiveness}
              onChange={onEdit('validatedEffectivenessScore', (event) => setEffectiveness(
                event.target.value === '' ? '' : Number(event.target.value),
              ))}
            />
          )}
        </Field>
        <Field
          label={t('drbfm.workflow.cards.feasibility', '実現性')}
          className="min-[600px]:w-[200px]"
          error={errors.feasibilityScore}
          errorLive={isTouched('feasibilityScore')}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              max={10}
              className={SCORE_INPUT}
              value={feasibility}
              onChange={onEdit('feasibilityScore', (event) => setFeasibility(
                event.target.value === '' ? '' : Number(event.target.value),
              ))}
            />
          )}
        </Field>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={isLoading || incomplete}
          onClick={save}
        >
          {t('drbfm.workflow.cards.adoptContent', '内容を採用')}
        </Button>
        <Button
          variant="outline"
          disabled={isLoading}
          onClick={() => onReview(measure.id, { reviewStatus: 'needs_info' })}
        >
          {t('drbfm.workflow.cards.needsInfo', '要確認')}
        </Button>
        <Button
          variant="outline"
          className={REJECT_BUTTON}
          disabled={isLoading}
          onClick={() => onReview(measure.id, { reviewStatus: 'rejected' })}
        >
          {t('drbfm.workflow.cards.reject', '却下')}
        </Button>
      </div>
    </div>
  );
};

export { CountermeasureCard };
